import React from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";

import { CounterColor, IRunCounter } from "@app/util/run-counters";

import styles from "@app/styles/dashboard/run-summary.module.scss";

interface IRunCounterSummaryProps<TRun> {
  run: TRun;
  counters: Array<IRunCounter<TRun>>;
}

const COLOR_CLASS: Record<CounterColor, string | undefined> = {
  blue: styles.badgeBlue,
  yellow: styles.badgeYellow,
  red: styles.badgeRed,
  green: styles.badgeGreen,
  grey: undefined
};

/**
 * Compact summary of a run's counters: only non-zero values, each with a full-label tooltip. Shared
 * by the page-sync and description dashboards — the counters differ, the presentation does not.
 */
const RunCounterSummary = <TRun,>({ run, counters }: IRunCounterSummaryProps<TRun>): JSX.Element => {
  const intl = useIntl();
  const visible = counters.filter((counter) => Number(run[counter.key] ?? 0) > 0);

  if (visible.length === 0) {
    return <div className={styles.empty}>—</div>;
  }

  return (
    <div className={styles.summary}>
      {visible.map((counter) => (
        <span
          key={String(counter.key)}
          className={clsx(styles.badge, COLOR_CLASS[counter.color])}
          title={intl.formatMessage({ id: counter.labelId })}
        >
          <strong>{String(run[counter.key])}</strong> {intl.formatMessage({ id: counter.shortId })}
        </span>
      ))}
    </div>
  );
};

export { RunCounterSummary };
